import React from "react";

import { Card, Fade, Slider, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";

import { PlayIconButton } from "./PlayIconButton";
import { PlaybackLoopButton } from "./PlaybackLoopButton";
import { PlaybackSpeedButton } from "./PlaybackSpeedButton";
import { PlaybackStoryButton } from "./PlaybackStoryButton";
import { usePlayer } from "./usePlayer";

// ============================================================================
// Styled Components
// ============================================================================

const Root = styled(Card)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: (theme.shape.borderRadius as number) * 5,
  color: theme.palette.text.secondary,
  overflow: "visible",
  position: "relative",
  padding: theme.spacing(0.5),
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(1),
  },
}));

// ============================================================================
// Utilities
// ============================================================================

const formatTimeLabel = (seconds: number): string => {
  return new Date(seconds * 1000).toISOString().slice(14, 19);
};

// ============================================================================
// Main Component
// ============================================================================

export const Player: React.FC<{ suppressNavElements?: boolean }> = ({
  suppressNavElements,
}) => {
  const {
    player,
    audioRef,
    handleSeekStart,
    handleSeekChange,
    handleSeekCommitted,
  } = usePlayer();

  // Don't render if no track available
  if (!player.track?.url) {
    return null;
  }

  return (
    <Fade in>
      <Root variant="outlined">
        <PlaybackStoryButton suppressNavElements={suppressNavElements} />
        <Stack direction="row" alignItems="center" spacing={{ xs: 1, md: 2 }}>
          <PlayIconButton soft story={player.story} />
          <Slider
            max={player.duration}
            min={0}
            onChange={(_event, value) => {
              if (!Array.isArray(value)) {
                handleSeekChange(value);
              }
            }}
            onChangeCommitted={(_event, value) => {
              if (!Array.isArray(value)) {
                handleSeekCommitted(value);
              }
            }}
            color={"primary"}
            onMouseDown={handleSeekStart}
            onTouchStart={handleSeekStart}
            size="small"
            value={player.currentTime}
            valueLabelDisplay="auto"
            valueLabelFormat={formatTimeLabel}
          />
          <Stack direction="row" alignItems="center" spacing={{ xs: 0, md: 1 }}>
            <PlaybackLoopButton />
            <PlaybackSpeedButton />
          </Stack>
        </Stack>
        <audio
          ref={audioRef}
          preload="auto"
          playsInline
          style={{ display: "none" }}
        />
      </Root>
    </Fade>
  );
};
